import { useEffect, useRef, useState } from "react";
import { FileText, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openPdf } from "@/lib/api";
import { STATUS, TONES } from "@/lib/format";
import { Button } from "@/components/ui/button";

export function PageHeader({ eyebrow, title, actions, children }: { eyebrow?: string; title: string; actions?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 fade-up">
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="page-title">{title}</h1>
        {children}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatusBadge({ status, testId }: { status: string; testId?: string }) {
  const [label, tone] = STATUS[status] || [status, "neutral"];
  return (
    <span data-testid={testId || `status-${status}`} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${TONES[tone]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function Panel({ title, actions, children, className = "", testId }: { title?: string; actions?: React.ReactNode; children: React.ReactNode; className?: string; testId?: string }) {
  return (
    <section className={`plate rounded-sm ${className}`} data-testid={testId}>
      {title && (
        <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.15em]">{title}</h2>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Empty({ text }: { text: string }) {
  return <div className="py-10 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</div>;
}

export function Loading() {
  return (
    <div className="flex justify-center py-16 text-muted-foreground" data-testid="loading">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

export function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="eyebrow">{label}</span>
      {children}
    </label>
  );
}

export function Info({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) {
  return (
    <div>
      <div className="eyebrow mb-1">{label}</div>
      <div className="text-sm" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

export function PdfButton({ kind, id, label, testId }: { kind: string; id: string; label: string; testId?: string }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await openPdf(kind, id);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Button variant="outline" onClick={go} disabled={busy} data-testid={testId || `pdf-${kind}-btn`}>
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
      {label}
    </Button>
  );
}

export function AuthImage({ fileId, className = "" }: { fileId: string; className?: string }) {
  const [src, setSrc] = useState<string>("");
  useEffect(() => {
    let url = "";
    api.get(`/files/${fileId}`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      setSrc(url);
    }).catch(() => {});
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);
  if (!src) return <div className={`animate-pulse bg-muted ${className}`} />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt="photo" className={`object-cover ${className}`} />
    </a>
  );
}

export function Photos({ entity, entityId, photos, onAdded, canUpload = true, big = false }: { entity: string; entityId: string; photos: string[]; onAdded: () => void; canUpload?: boolean; big?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("entity", entity);
      fd.append("entity_id", entityId);
      fd.append("file", f);
      try {
        await api.post("/files", fd);
      } catch (e) {
        toast.error(errMsg(e));
      }
    }
    setBusy(false);
    onAdded();
  };
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="photos-grid">
      {photos.map((p) => <AuthImage key={p} fileId={p} className="aspect-square w-full rounded-sm border border-border" />)}
      {canUpload && (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className={`flex aspect-square flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-foreground ${big ? "text-base" : "text-xs"}`}
          data-testid="photo-upload-btn"
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className={big ? "h-8 w-8" : "h-5 w-5"} />}
          Ajouter
        </button>
      )}
      <input ref={input} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-file-input" />
    </div>
  );
}

export function Table({ head, children, testId }: { head: string[]; children: React.ReactNode; testId?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="border-b border-border text-left">
            {head.map((h) => <th key={h} className="eyebrow px-3 py-2.5 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export const rowCls = "border-b border-border/60 transition-colors hover:bg-accent/60 cursor-pointer";
export const cellCls = "px-3 py-3 align-middle";
